import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraFormField, SpecraInput, SpecraSelect, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { PageBody, PageHero } from '../../components/layout';
import { EconomicsFigures } from './components/EconomicsFigures';
import { LANDSCAPE_REPORT_KEY, useExportLandscapeReport, useGenerateLandscapeReport, useLandscapeReport, useSaveReportPresentation } from './hooks/useLandscapeReports';
import { downloadCsv } from './portfolioEconomics';
const today = ()=>new Date().toISOString().slice(0, 10);
function Unavailable({ reportId }) {
    return /*#__PURE__*/ _jsx(PageBody, {
        children: /*#__PURE__*/ _jsx("div", {
            "data-report-id": reportId,
            children: /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                "data-testid": Testids.landscapeReports.unavailable,
                children: "Review briefs are unavailable to you or could not be loaded."
            })
        })
    });
}
export function LandscapeReportPage() {
    const { id = '', reportId = '' } = useParams();
    const queryClient = useQueryClient();
    const [versionId, setVersionId] = useState('');
    const report = useLandscapeReport(reportId, versionId);
    const savePresentation = useSaveReportPresentation();
    const exportReport = useExportLandscapeReport();
    const regenerate = useGenerateLandscapeReport(id);
    const [saved, setSaved] = useState(false);
    const [headline, setHeadline] = useState('');
    const [note, setNote] = useState('');
    const [regenerateAsOf, setRegenerateAsOf] = useState(today());
    const loadedVersionId = report.data?.version?.id;
    const loadedHeadline = report.data?.version?.presentation?.headline ?? '';
    const loadedNote = report.data?.version?.presentation?.note ?? '';
    useEffect(()=>{
        setHeadline(loadedHeadline);
        setNote(loadedNote);
        setSaved(false);
    }, [
        loadedVersionId,
        loadedHeadline,
        loadedNote
    ]);
    if (report.isLoading) {
        return /*#__PURE__*/ _jsx(PageBody, {
            children: /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                children: "Loading…"
            })
        });
    }
    if (report.error || !report.data?.version) {
        return /*#__PURE__*/ _jsx(Unavailable, {
            reportId: reportId
        });
    }
    const version = report.data.version;
    const versions = report.data.versions;
    const content = version.content;
    const fail = (what)=>()=>toast.error(`Could not ${what}`);
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": Testids.landscapeReports.reportPage,
        "data-report-id": reportId,
        "data-version-id": version.id,
        "data-version": version.version,
        children: /*#__PURE__*/ _jsxs(PageBody, {
            children: [
                /*#__PURE__*/ _jsx(PageHero, {
                    title: version.title || 'Review brief',
                    subtitle: `Generated by ${version.createdBy} on ${version.createdAt}`,
                    actions: /*#__PURE__*/ _jsx(Link, {
                        to: `/landscapes/${id}/briefs`,
                        className: "text-sm text-muted-foreground underline",
                        "data-testid": Testids.landscapeReports.backLink,
                        children: "Back to briefs"
                    })
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-6",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "rounded-md border border-border p-3 text-sm",
                            "data-testid": Testids.landscapeReports.asOf,
                            children: [
                                "Point-in-time brief — figures as of ",
                                content?.asOf,
                                ". Later changes to the underlying records do not alter this version."
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("section", {
                            className: "space-y-2 rounded-md border border-dashed border-border p-3",
                            children: [
                                /*#__PURE__*/ _jsx("h2", {
                                    className: "text-sm font-medium",
                                    children: "Regenerate as of"
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm text-muted-foreground",
                                    children: "This freezes a new version as of the date below. The current version is kept and stays viewable."
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex flex-wrap items-end gap-3",
                                    children: [
                                        /*#__PURE__*/ _jsx(SpecraFormField, {
                                            label: "As of",
                                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                                type: "date",
                                                value: regenerateAsOf,
                                                onChange: (e)=>setRegenerateAsOf(e.target.value),
                                                "data-testid": Testids.landscapeReports.regenerateAsOfInput
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                            size: "sm",
                                            disabled: regenerate.isPending,
                                            "data-testid": Testids.landscapeReports.regenerateButton,
                                            onClick: ()=>regenerate.mutate({
                                                    templateId: content?.templateId ?? '',
                                                    asOf: regenerateAsOf,
                                                    windowDays: content?.windowDays ?? 0,
                                                    preview: false,
                                                    reportId,
                                                    title: version.title
                                                }, {
                                                    onSuccess: (data)=>{
                                                        queryClient.invalidateQueries({
                                                            queryKey: [
                                                                ...LANDSCAPE_REPORT_KEY,
                                                                reportId
                                                            ]
                                                        });
                                                        if (data.version) setVersionId(data.version.id);
                                                    },
                                                    onError: fail('regenerate this brief')
                                                }),
                                            children: "Regenerate"
                                        })
                                    ]
                                })
                            ]
                        }),
                        versions.length > 1 && /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Version",
                            children: /*#__PURE__*/ _jsx(SpecraSelect, {
                                size: "sm",
                                value: versionId || version.id,
                                onChange: (e)=>setVersionId(e.target.value),
                                "data-testid": Testids.landscapeReports.versionSelect,
                                children: versions.map((v)=>/*#__PURE__*/ _jsxs("option", {
                                        value: v.id,
                                        children: [
                                            "v",
                                            v.version,
                                            " — ",
                                            v.asOf
                                        ]
                                    }, v.id))
                            })
                        }),
                        content?.figures && /*#__PURE__*/ _jsxs("section", {
                            "data-testid": Testids.landscapeReports.figures,
                            children: [
                                /*#__PURE__*/ _jsx("h2", {
                                    className: "mb-2 text-sm font-medium",
                                    children: "Figures"
                                }),
                                /*#__PURE__*/ _jsx(EconomicsFigures, {
                                    figures: content.figures
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("section", {
                            className: "space-y-1",
                            "data-testid": Testids.landscapeReports.coverage,
                            children: [
                                /*#__PURE__*/ _jsx("h2", {
                                    className: "text-sm font-medium",
                                    children: "Coverage"
                                }),
                                /*#__PURE__*/ _jsxs("p", {
                                    className: "text-sm",
                                    children: [
                                        content?.opportunitiesInScope.toString(),
                                        " opportunities in scope,",
                                        ' ',
                                        content?.opportunitiesMissingValues.toString(),
                                        " missing a recorded value"
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("ul", {
                                    className: "text-sm text-muted-foreground",
                                    children: content?.coverage.map((c)=>/*#__PURE__*/ _jsxs("li", {
                                            children: [
                                                c.kind,
                                                " (",
                                                c.currency,
                                                "): ",
                                                c.knownCount.toString(),
                                                " of ",
                                                c.totalCount.toString(),
                                                ' ',
                                                "known"
                                            ]
                                        }, `${c.kind}:${c.currency}`))
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("section", {
                            className: "space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx("h2", {
                                    className: "text-sm font-medium",
                                    children: "Value definitions"
                                }),
                                content?.valueDefinitions.map((d)=>/*#__PURE__*/ _jsxs("div", {
                                        "data-kind": d.kind,
                                        "data-testid": Testids.landscapeReports.valueDefinition,
                                        className: "text-sm",
                                        children: [
                                            /*#__PURE__*/ _jsxs("span", {
                                                className: "font-medium",
                                                children: [
                                                    d.label,
                                                    ":"
                                                ]
                                            }),
                                            " ",
                                            d.definition
                                        ]
                                    }, d.kind))
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("section", {
                            className: "space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx("h2", {
                                    className: "text-sm font-medium",
                                    children: "Selected source versions — the exact frozen rows the figures above were computed from"
                                }),
                                /*#__PURE__*/ _jsx("ul", {
                                    className: "text-sm text-muted-foreground",
                                    children: content?.selectedVersions.map((s)=>/*#__PURE__*/ _jsxs("li", {
                                            "data-id": s.id,
                                            "data-source-kind": s.sourceKind,
                                            "data-testid": Testids.landscapeReports.selectedVersion,
                                            children: [
                                                s.sourceKind,
                                                " · ",
                                                s.kind,
                                                " ",
                                                s.amount,
                                                " ",
                                                s.currency,
                                                " · as of ",
                                                s.asOf,
                                                " ·",
                                                ' ',
                                                s.title || s.provenance
                                            ]
                                        }, s.id))
                                })
                            ]
                        }),
                        content?.templateId === 'review-brief-v1' && /*#__PURE__*/ _jsxs("section", {
                            className: "space-y-1",
                            "data-testid": Testids.landscapeReports.changesSection,
                            children: [
                                /*#__PURE__*/ _jsx("h2", {
                                    className: "text-sm font-medium",
                                    children: "Membership changes"
                                }),
                                content.changes.length === 0 ? /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm text-muted-foreground",
                                    children: "No membership changes in this window."
                                }) : /*#__PURE__*/ _jsx("ul", {
                                    className: "text-sm text-muted-foreground",
                                    children: content.changes.map((c)=>/*#__PURE__*/ _jsxs("li", {
                                            "data-testid": Testids.landscapeReports.changeRow,
                                            children: [
                                                c.action,
                                                " · ",
                                                c.basis,
                                                " · ",
                                                c.actor,
                                                " · ",
                                                c.occurredAt,
                                                c.reason ? ` · ${c.reason}` : ''
                                            ]
                                        }, c.id))
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("section", {
                            className: "space-y-2 rounded-md border border-dashed border-border p-3",
                            children: [
                                /*#__PURE__*/ _jsx("h2", {
                                    className: "text-sm font-medium",
                                    children: "Presentation edits change how this brief reads. They never change the figures or any source record."
                                }),
                                /*#__PURE__*/ _jsx(SpecraFormField, {
                                    label: "Headline",
                                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                                        value: headline,
                                        onChange: (e)=>{
                                            setHeadline(e.target.value);
                                            setSaved(false);
                                        },
                                        "data-testid": Testids.landscapeReports.presentationHeadline
                                    })
                                }),
                                /*#__PURE__*/ _jsx(SpecraFormField, {
                                    label: "Note",
                                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                                        value: note,
                                        onChange: (e)=>{
                                            setNote(e.target.value);
                                            setSaved(false);
                                        },
                                        "data-testid": Testids.landscapeReports.presentationNote
                                    })
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    disabled: savePresentation.isPending,
                                    "data-testid": Testids.landscapeReports.presentationSave,
                                    onClick: ()=>savePresentation.mutate({
                                            reportId,
                                            versionId: version.id,
                                            headline,
                                            note
                                        }, {
                                            onSuccess: ()=>setSaved(true),
                                            onError: fail('save presentation')
                                        }),
                                    children: "Save presentation"
                                }),
                                saved && /*#__PURE__*/ _jsx("span", {
                                    className: "text-sm text-muted-foreground",
                                    "data-testid": Testids.landscapeReports.presentationSaved,
                                    children: "Saved"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "outline",
                            size: "sm",
                            disabled: exportReport.isPending,
                            "data-testid": Testids.landscapeReports.exportButton,
                            onClick: ()=>exportReport.mutate(version.id, {
                                    onSuccess: (res)=>{
                                        const csv = new TextDecoder().decode(res.content);
                                        downloadCsv(res.filename, csv);
                                    },
                                    onError: fail('export this brief')
                                }),
                            children: "Export CSV"
                        })
                    ]
                })
            ]
        })
    });
}
